import { useEffect, useState, useRef } from 'react'
import { useYear } from '../auth/YearContext'
import { subscribeReceipts, deleteReceiptCloud, aiAnalyzeReceipt, updateReceiptFields, computeQuality, type CloudReceipt, type ReceiptQuality } from '../lib/cloudStore'
import { invoiceNoFromFilename, receiptListTitle } from '../lib/receipts'
import { WinButtons, currentSize, type CardSizeProps } from './WinButtons'

const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 2 })
const fmt0 = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

const CAT_LABEL: Record<CloudReceipt['category'], string> = {
  kundfakturor: 'Kundfaktura',
  leverantorsfakturor: 'Leverant\u00f6rsfaktura',
  utlagg: 'Utl\u00e4gg',
}

const CAT_COLOR: Record<CloudReceipt['category'], string> = {
  kundfakturor: '#10b981',
  leverantorsfakturor: '#ef4444',
  utlagg: '#3b82f6',
}

const Q_COLOR: Record<ReceiptQuality, string> = {
  red: '#f59e0b',
  orange: '#f97316',
  yellow: '#eab308',
  green: '#10b981',
  verified: '#10b981',
}
const Q_LABEL: Record<ReceiptQuality, string> = {
  red: 'Saknar data',
  orange: 'Grunddata (datum/belopp)',
  yellow: 'Datum+belopp+moms OK',
  green: 'Komplett bokf\u00f6ring',
  verified: 'Manuellt verifierad',
}

const VAT_LABEL: Record<number, string> = {
  0.25: '25%',
  0.12: '12%',
  0.06: '6%',
  0: '0% (momsfri)',
}

/** Konvertera valuestring till nummer (svenska + engelska decimal-tecken).
 *  Returnerar undefined om input r tomt eller ogiltigt (NaN).
 *  Anvnds fr alla parseFloat/parseInt-anrop fr att undvika att NaN skickas till Firestore.
 */
function parseNum(s: string | undefined): number | undefined {
  if (!s) return undefined
  const t = s.trim()
  if (!t) return undefined
  const n = parseFloat(t.replace(/\s/g, '').replace(',', '.'))
  return isNaN(n) ? undefined : n
}

interface Props extends CardSizeProps { companyId: string; companyName: string }

export function ReceiptsCard(props: Props) {
  const { companyId, companyName } = props
  const size = currentSize(props)
  const { year } = useYear()
  const [list, setList] = useState<CloudReceipt[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<CloudReceipt | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [reanalyzing, setReanalyzing] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<string | null>(null)
  const [filterCat, setFilterCat] = useState<CloudReceipt['category'] | 'all'>('all')
  const [batchRunning, setBatchRunning] = useState(false)
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 })
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'quality' | 'name'>('date')
  const [searchQuery, setSearchQuery] = useState('')

  // Manuell granskning popup
  const [reviewReceipt, setReviewReceipt] = useState<CloudReceipt | null>(null)

  async function reanalyze(r: CloudReceipt) {
    setReanalyzing((s) => new Set(s).add(r.id))
    try {
      // force=false → fyller bara i tomma fält, rör inte manuellt ifyllda
      await aiAnalyzeReceipt(companyId, r, false)
    } catch { /* quality s\u00e4tts till red i aiAnalyzeReceipt */ }
    setReanalyzing((s) => { const n = new Set(s); n.delete(r.id); return n })
  }

  const batchControllerRef = useRef<AbortController | null>(null)

  async function reanalyzeAll() {
    const targets = filtered.filter((r) => r.quality !== 'verified')
    if (targets.length === 0) return

    const controller = new AbortController()
    batchControllerRef.current = controller
    setBatchRunning(true)
    setBatchProgress({ done: 0, total: targets.length })

    // 2026-05-24: raised from 3 → 10 per request. /api/storage-extract has
    // 3-retry backoff on 429, so up to 30 calls/min worst case.
    const CONCURRENCY = 10
    const TIMEOUT_MS = 30_000
    let done = 0

    async function processOne(r: CloudReceipt) {
      if (controller.signal.aborted) return
      // Per-kvitto timeout via egen AbortController
      const perItem = new AbortController()
      const timer = setTimeout(() => perItem.abort(), TIMEOUT_MS)
      // Avbryt per-item om hela batchen stoppas
      const onBatchAbort = () => perItem.abort()
      controller.signal.addEventListener('abort', onBatchAbort)
      try {
        await aiAnalyzeReceipt(companyId, r, false, perItem.signal)
      } catch { /* timeout, abort, or API error — hoppa vidare */ }
      clearTimeout(timer)
      controller.signal.removeEventListener('abort', onBatchAbort)
      done++
      setBatchProgress({ done, total: targets.length })
    }

    for (let i = 0; i < targets.length; i += CONCURRENCY) {
      if (controller.signal.aborted) break
      const chunk = targets.slice(i, i + CONCURRENCY)
      await Promise.all(chunk.map(processOne))
    }

    batchControllerRef.current = null
    setBatchRunning(false)
    setSortBy('quality')
    // Öppna det första kvittot som behöver fixas
    const worstFirst = [...filtered].sort((a, b) =>
      Q_ORDER[a.quality || computeQuality(a)] - Q_ORDER[b.quality || computeQuality(b)]
    )
    const needsFix = worstFirst.find((r) => {
      const q = r.quality || computeQuality(r)
      return q !== 'green' && q !== 'verified'
    })
    if (needsFix) setExpanded(needsFix.id)
  }

  function stopBatch() {
    batchControllerRef.current?.abort()
  }

  useEffect(() => {
    setErr(null)
    try {
      return subscribeReceipts(companyId, year, setList)
    } catch (e) {
      setErr((e as Error).message)
    }
  }, [companyId, year])

  const Q_ORDER: Record<ReceiptQuality, number> = { red: 0, orange: 1, yellow: 2, green: 3, verified: 4 }
  const baseFiltered = filterCat === 'all' ? list : list.filter((r) => r.category === filterCat)
  const qNeedle = searchQuery.trim().toLowerCase()
  const searched = qNeedle
    ? baseFiltered.filter((r) => {
        const hay = [
          r.filename, r.title, r.date, r.currency,
          r.amount != null ? String(r.amount) : '',
          invoiceNoFromFilename(r.filename) || '',
        ].join(' ').toLowerCase()
        return hay.includes(qNeedle)
      })
    : baseFiltered
  const filtered = [...searched].sort((a, b) => {
    switch (sortBy) {
      case 'date': return (b.date || '').localeCompare(a.date || '')
      case 'amount': return (b.amount ?? 0) - (a.amount ?? 0)
      case 'quality': return Q_ORDER[a.quality || computeQuality(a)] - Q_ORDER[b.quality || computeQuality(b)]
      case 'name': return (a.title || a.filename).localeCompare(b.title || b.filename, 'sv')
      default: return 0
    }
  })

  const latestDate = list.reduce<string | null>((best, r) => {
    if (!r.date) return best
    if (!best) return r.date
    return r.date > best ? r.date : best
  }, null)

  // Summor
  const totalAmount = filtered.reduce((s, r) => s + (r.amount ?? 0), 0)
  const catCounts = {
    kundfakturor: list.filter((r) => r.category === 'kundfakturor').length,
    leverantorsfakturor: list.filter((r) => r.category === 'leverantorsfakturor').length,
    utlagg: list.filter((r) => r.category === 'utlagg').length,
  }

  function exportCSV() {
    const SEP = ';'
    const headers = ['Datum', 'Beskrivning', 'Kategori', 'Belopp inkl moms', 'Belopp exkl moms', 'Momsbelopp', 'Momssats', 'Valuta', 'Konto', 'Kontobeskrivning', 'Kvalitet', 'Filnamn']
    const csvField = (v: string) => '"' + v.replace(/"/g, '""') + '"'
    const rows = filtered.map((r) => {
      const q = r.quality || computeQuality(r)
      return [
        r.date || '',
        receiptListTitle(r),
        CAT_LABEL[r.category],
        r.amount != null ? String(r.amount).replace('.', ',') : '',
        r.amountExVat != null ? String(r.amountExVat).replace('.', ',') : '',
        r.vatAmount != null ? String(r.vatAmount).replace('.', ',') : '',
        r.vatRate != null ? String(r.vatRate * 100).replace('.', ',') + '%' : '',
        r.currency || 'SEK',
        r.accountCode != null ? String(r.accountCode) : '',
        r.accountLabel || '',
        Q_LABEL[q],
        r.filename,
      ].map(csvField).join(SEP)
    })
    const bom = '\uFEFF' // UTF-8 BOM så Excel öppnar korrekt
    const csv = bom + headers.map(csvField).join(SEP) + '\n' + rows.join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = companyName + ' - Kvitton ' + year + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  async function doDelete() {
    if (!confirmDelete) return
    setDeleting(true)
    try {
      await deleteReceiptCloud(companyId, confirmDelete)
      setConfirmDelete(null)
      if (expanded === confirmDelete.id) setExpanded(null)
    } catch (e) {
      setErr('Kunde inte ta bort: ' + (e as Error).message)
      setConfirmDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  if (size === "min") {
    return (
      <div className="card card--collapsed" style={{ borderLeftColor: '#3b82f6' }}>
        <div className="card__header-row">
          <span className="card__label" style={{ margin: 0 }}>Kvitton</span>
          <span className="card__mini-value">{list.length + ' st' + (latestDate ? ' \u00b7 ' + latestDate : '')}</span>
          <WinButtons current="min" {...props} />
        </div>
      </div>
    )
  }

  return (
    <div className={'card card--accent' + (size === 'med' ? ' card--medium' : '')} style={{ borderLeftColor: '#3b82f6' }}>
      <div className="card__header-row">
        <div className="card__label" style={{ margin: 0 }}>{'Kvitton & fakturor (' + year + ') \u2014 live'}</div>
        <WinButtons current={size} {...props} />
      </div>
      <div className="card__value" style={{ fontSize: '1.5rem' }}>
        {filtered.length + ' ' + (filtered.length === 1 ? 'post' : 'poster')}
        {filterCat !== 'all' && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 8 }}>{'av ' + list.length}</span>}
      </div>
      <div className="card__sub">
        {latestDate ? 'Senaste: ' + latestDate + ' \u00b7 Summa: ' + fmt0(totalAmount) + ' kr' : 'Uppdateras i realtid'}
      </div>

      {/* Category filter tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
        {([
          ['all', 'Alla (' + list.length + ')'],
          ['kundfakturor', 'Kundfakturor (' + catCounts.kundfakturor + ')'],
          ['leverantorsfakturor', 'Lev.fakturor (' + catCounts.leverantorsfakturor + ')'],
          ['utlagg', 'Utl\u00e4gg (' + catCounts.utlagg + ')'],
        ] as [CloudReceipt['category'] | 'all', string][]).map(([cat, label]) => (
          <button
            key={cat}
            onClick={() => { setFilterCat(cat); setExpanded(null) }}
            style={{
              background: filterCat === cat ? 'rgba(255,255,255,0.12)' : 'transparent',
              border: '1px solid ' + (filterCat === cat ? 'var(--primary)' : 'var(--border)'),
              borderRadius: 6, padding: '3px 8px', fontSize: '0.7rem',
              color: filterCat === cat ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {cat !== 'all' && (
              <span style={{
                display: 'inline-block', width: 6, height: 6, borderRadius: 3,
                background: CAT_COLOR[cat as CloudReceipt['category']], marginRight: 4, verticalAlign: 'middle',
              }} />
            )}
            {label}
          </button>
        ))}
      </div>

      {/* Sort + batch actions */}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Sök nr / filnamn / belopp…"
          style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '3px 8px', fontSize: '0.7rem',
            color: 'var(--text)', minWidth: 180, flex: '1 1 160px',
          }}
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '3px 6px', fontSize: '0.7rem',
            color: 'var(--text-muted)', cursor: 'pointer',
          }}
        >
          <option value="date">Sortera: Datum</option>
          <option value="amount">Sortera: Belopp</option>
          <option value="quality">Sortera: Kvalitet</option>
          <option value="name">Sortera: Namn</option>
        </select>
        <button
          onClick={reanalyzeAll}
          disabled={batchRunning}
          style={{
            background: batchRunning ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.06)',
            border: '1px solid var(--border)', borderRadius: 6,
            padding: '3px 10px', fontSize: '0.7rem',
            color: batchRunning ? 'var(--text-muted)' : '#fff',
            cursor: batchRunning ? 'wait' : 'pointer',
            transition: 'all 0.15s',
          }}
        >
          {batchRunning
            ? 'xAI ' + batchProgress.done + '/' + batchProgress.total + '...'
            : 'xAI Omanalysera alla'}
        </button>
        {batchRunning && (
          <button
            onClick={stopBatch}
            style={{
              background: 'transparent', border: '1px solid var(--danger)',
              borderRadius: 6, padding: '3px 10px', fontSize: '0.7rem',
              color: 'var(--danger)', cursor: 'pointer',
            }}
          >
            Stoppa
          </button>
        )}
        <button
          onClick={exportCSV}
          style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '3px 10px', fontSize: '0.7rem',
            color: '#fff', cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          {'Exportera CSV'}
        </button>
      </div>

      {err && (
        <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: 8 }}>
          {err}
        </div>
      )}

      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'grid', placeItems: 'center', zIndex: 9999,
        }} onClick={() => !deleting && setConfirmDelete(null)}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '1.5rem', maxWidth: 420, width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem', color: 'var(--danger)' }}>
              {'\ud83d\uddd1\ufe0f Ta bort kvitto?'}
            </div>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              {confirmDelete.title || confirmDelete.filename}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              {(confirmDelete.date || '') + (confirmDelete.amount != null ? ' \u00b7 ' + fmt(confirmDelete.amount) + ' ' + (confirmDelete.currency || '') : '')}
              <br />
              {'Filen tas bort fr\u00e5n b\u00e5de Firestore och Storage. Detta g\u00e5r inte att \u00e5ngra.'}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="logout-btn" onClick={() => setConfirmDelete(null)} disabled={deleting}>
                Avbryt
              </button>
              <button
                className="login__btn"
                style={{ width: 'auto', padding: '0.5rem 1rem', background: 'var(--danger)', color: '#fff' }}
                onClick={doDelete}
                disabled={deleting}
              >
                {deleting ? 'Tar bort...' : '\ud83d\uddd1\ufe0f Ta bort'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card__divider" />

      {filtered.length === 0 ? (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          {searchQuery.trim()
            ? 'Ingen träff på “' + searchQuery.trim() + '”.'
            : filterCat === 'all'
            ? 'Ladda upp PDF eller bild via \ud83d\udce5 Import. De syns h\u00e4r direkt.'
            : 'Inga ' + (filterCat === 'kundfakturor' ? 'kundfakturor' : filterCat === 'leverantorsfakturor' ? 'leverant\u00f6rsfakturor' : 'utl\u00e4gg') + ' \u00e4nnu.'}
        </div>
      ) : (
        <div style={{ maxHeight: size === 'max' ? 600 : 400, overflowY: 'auto' }}>
          {filtered.map((r) => {
            const isOpen = expanded === r.id
            const q = r.quality || computeQuality(r)
            return (
              <div key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                {/* Compact row */}
                <div
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.55rem 0', cursor: 'pointer',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Category dot */}
                  <span style={{
                    width: 8, height: 8, borderRadius: 4,
                    background: CAT_COLOR[r.category], flexShrink: 0,
                  }} />
                  {/* Quality dot */}
                  <span
                    title={Q_LABEL[q]}
                    style={{
                      width: 8, height: 8, borderRadius: 4,
                      background: Q_COLOR[q], flexShrink: 0, position: 'relative',
                    }}
                  >
                    {q === 'verified' && (
                      <span style={{
                        position: 'absolute', top: -3, left: -1,
                        fontSize: '0.55rem', color: '#fff', fontWeight: 700, lineHeight: 1,
                      }}>{'\u2713'}</span>
                    )}
                  </span>
                  {/* Title + sub info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.85rem', whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={r.filename}>
                      {receiptListTitle(r)}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      {CAT_LABEL[r.category]}
                      {r.date ? ' \u00b7 ' + r.date : ''}
                      {r.amount != null ? ' \u00b7 ' + fmt0(r.amount) + ' ' + (r.currency || '') : ''}
                    </div>
                  </div>
                  {/* Quick actions in compact row */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setReviewReceipt(r) }}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: q === 'verified' ? '#10b981' : 'var(--text-muted)', fontSize: '0.7rem', flexShrink: 0,
                      padding: '2px 5px', borderRadius: 4, lineHeight: 1,
                      transition: 'color 0.15s, background 0.15s',
                    }}
                    title={'Manuell granskning'}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#10b981'; e.currentTarget.style.background = 'rgba(16,185,129,0.1)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = q === 'verified' ? '#10b981' : 'var(--text-muted)'; e.currentTarget.style.background = 'transparent' }}
                  >
                    {q === 'verified' ? '\u2713' : '\u270e'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); reanalyze(r) }}
                    disabled={reanalyzing.has(r.id)}
                    style={{
                      background: 'transparent', border: 'none', cursor: reanalyzing.has(r.id) ? 'wait' : 'pointer',
                      color: 'var(--text-muted)', fontSize: '0.7rem', flexShrink: 0,
                      padding: '2px 5px', borderRadius: 4, lineHeight: 1,
                      transition: 'color 0.15s, background 0.15s',
                      opacity: reanalyzing.has(r.id) ? 0.5 : 1,
                    }}
                    title={'AI-fyller tomma f\u00e4lt (r\u00f6r inte manuella)'}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent' }}
                  >
                    {reanalyzing.has(r.id) ? '...' : 'xAI'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(r) }}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', fontSize: '0.85rem', flexShrink: 0,
                      padding: '2px 4px', borderRadius: 4, lineHeight: 1,
                      transition: 'color 0.15s',
                    }}
                    title={'Ta bort ' + r.filename}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                  >
                    {'\ud83d\uddd1\ufe0f'}
                  </button>
                  {/* Expand indicator */}
                  <span style={{
                    fontSize: '0.7rem', color: 'var(--text-muted)', flexShrink: 0,
                    transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
                  }}>{'\u25b6'}</span>
                </div>

                {/* Expanded detail panel */}
                {isOpen && (
                  <ReceiptDetail
                    r={r}
                    q={q}
                    companyId={companyId}
                    reanalyzing={reanalyzing.has(r.id)}
                    onReanalyze={() => reanalyze(r)}
                    onDelete={() => setConfirmDelete(r)}
                  />
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Legend */}
      <div className="card__divider" />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
        {([
          ['red', 'Saknar data'],
          ['orange', 'Grunddata'],
          ['yellow', 'Datum+belopp+moms'],
          ['green', 'Komplett'],
          ['verified', 'Verifierad'],
        ] as [ReceiptQuality, string][]).map(([q, label]) => (
          <span key={q} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: Q_COLOR[q], display: 'inline-block', position: 'relative',
            }}>
              {q === 'verified' && (
                <span style={{ position: 'absolute', top: -2, left: 0, fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>{'\u2713'}</span>
              )}
            </span>
            {label}
          </span>
        ))}
      </div>
      <div style={{ marginTop: '0.4rem' }}>
        <a
          href="/kvittokontroll-guide.pdf"
          target="_blank"
          rel="noopener"
          style={{ fontSize: '0.7rem', color: 'var(--primary)' }}
        >
          {'Dokumentation \u2197'}
        </a>
      </div>

      {/* Manuell granskningspopup */}
      {reviewReceipt && (
        <ReviewPopup
          receipt={reviewReceipt}
          companyId={companyId}
          onClose={() => setReviewReceipt(null)}
          onNext={() => {
            // Hitta nästa overifierat kvitto
            const idx = filtered.findIndex((r) => r.id === reviewReceipt.id)
            const next = filtered.slice(idx + 1).find((r) => {
              const q = r.quality || computeQuality(r)
              return q !== 'verified'
            })
            if (next) setReviewReceipt(next)
            else setReviewReceipt(null)
          }}
        />
      )}
    </div>
  )
}

// ============================================================
// Manuell granskningspopup — side-by-side: förhandsgranskning + redigerbart formulär
// ============================================================

function ReviewPopup({ receipt, companyId, onClose, onNext }: {
  receipt: CloudReceipt
  companyId: string
  onClose: () => void
  onNext: () => void
}) {
  const [date, setDate] = useState(receipt.date ?? '')
  const [title, setTitle] = useState(receipt.title ?? '')
  const [amount, setAmount] = useState(receipt.amount != null ? String(receipt.amount) : '')
  const [amountExVat, setAmountExVat] = useState(receipt.amountExVat != null ? String(receipt.amountExVat) : '')
  const [vatAmount, setVatAmount] = useState(receipt.vatAmount != null ? String(receipt.vatAmount) : '')
  const [vatRate, setVatRate] = useState(receipt.vatRate != null ? String(receipt.vatRate * 100) : '')
  const [currency, setCurrency] = useState(receipt.currency ?? 'SEK')
  const [accountCode, setAccountCode] = useState(receipt.accountCode != null ? String(receipt.accountCode) : '')
  const [accountLabel, setAccountLabel] = useState(receipt.accountLabel ?? '')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  // Resizable split — percentage for left (preview) panel
  const [splitPct, setSplitPct] = useState(60)
  const [isDragging, setIsDragging] = useState(false)
  const dragging = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  function onDragStart(e: React.MouseEvent) {
    e.preventDefault()
    dragging.current = true
    setIsDragging(true)
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const pct = ((ev.clientX - rect.left) / rect.width) * 100
      setSplitPct(Math.min(80, Math.max(25, pct)))
    }
    const onUp = () => { dragging.current = false; setIsDragging(false); document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // Sync fields when receipt changes (navigating next)
  useEffect(() => {
    setDate(receipt.date ?? '')
    setTitle(receipt.title ?? '')
    setAmount(receipt.amount != null ? String(receipt.amount) : '')
    setAmountExVat(receipt.amountExVat != null ? String(receipt.amountExVat) : '')
    setVatAmount(receipt.vatAmount != null ? String(receipt.vatAmount) : '')
    setVatRate(receipt.vatRate != null ? String(receipt.vatRate * 100) : '')
    setCurrency(receipt.currency ?? 'SEK')
    setAccountCode(receipt.accountCode != null ? String(receipt.accountCode) : '')
    setAccountLabel(receipt.accountLabel ?? '')
    setMsg(null)
  }, [receipt.id])

  // Keyboard: Escape closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  function autoCalcVat(newAmount?: string, newRate?: string) {
    const amt = parseNum(newAmount ?? amount)
    let rate = parseNum(newRate ?? vatRate)
    if (rate != null && rate >= 1) rate = rate / 100
    if (amt != null && rate != null && rate > 0) {
      const vat = amt * rate / (1 + rate)
      setVatAmount(vat.toFixed(2))
      setAmountExVat((amt - vat).toFixed(2))
    }
  }

  async function onSave(andNext: boolean) {
    setSaving(true)
    setMsg(null)
    try {
      let rateVal = parseNum(vatRate)
      if (rateVal != null && rateVal >= 1) rateVal = rateVal / 100
      const accNum = parseNum(accountCode)
      await updateReceiptFields(companyId, receipt.id, {
        date: date || undefined,
        title: title || undefined,
        amount: parseNum(amount),
        amountExVat: parseNum(amountExVat),
        vatAmount: parseNum(vatAmount),
        vatRate: rateVal,
        currency: currency || undefined,
        accountCode: accNum != null ? Math.round(accNum) : undefined,
        accountLabel: accountLabel || undefined,
      })
      if (andNext) {
        onNext()
      } else {
        setMsg('Sparat och verifierat!')
        setTimeout(() => setMsg(null), 2000)
      }
    } catch (e) {
      setMsg('Fel: ' + (e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const isImage = receipt.mime?.startsWith('image/')
  const isPdf = receipt.mime === 'application/pdf'
  const q = receipt.quality || computeQuality(receipt)
  const missing: string[] = []
  if (!receipt.date) missing.push('datum')
  if (!receipt.title) missing.push('beskrivning')
  if (receipt.amount == null) missing.push('belopp')
  if (receipt.vatRate == null) missing.push('momssats')
  if (receipt.accountCode == null) missing.push('konto')

  const fieldStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
    borderRadius: 6, padding: '0.45rem 0.6rem', fontSize: '0.9rem',
    color: '#fff', width: '100%', outline: 'none',
  }
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.65rem', textTransform: 'uppercase',
    letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 2,
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', flexDirection: 'column',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      {/* Full-viewport panel — nearly edge-to-edge */}
      <div
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, margin: '0.5rem',
          flex: 1, display: 'flex', flexDirection: 'column',
          overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.6rem 1rem', borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>Manuell granskning</div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{receipt.filename}</span>
            {missing.length > 0 && (
              <span style={{ fontSize: '0.75rem', color: '#f97316', background: 'rgba(249,115,22,0.1)', padding: '2px 8px', borderRadius: 4 }}>
                {'Saknar: ' + missing.join(', ')}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: '0.75rem', padding: '2px 8px', borderRadius: 4,
              background: Q_COLOR[q] + '22', color: Q_COLOR[q], fontWeight: 600,
            }}>
              {q === 'verified' && '\u2713 '}{Q_LABEL[q]}
            </span>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.3rem', cursor: 'pointer', padding: '0.25rem',
            }}>{'\u2715'}</button>
          </div>
        </div>

        {/* Body — resizable side by side */}
        <div ref={containerRef} style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
          {/* Transparent overlay during drag — prevents iframes from stealing mouse events */}
          {isDragging && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'col-resize' }} />
          )}
          {/* Left: file preview (A4 style) */}
          <div style={{
            width: splitPct + '%', flexShrink: 0,
            display: 'flex', flexDirection: 'column', background: '#111827',
            overflow: 'hidden',
          }}>
            {receipt.downloadUrl ? (
              isPdf ? (
                <iframe src={receipt.downloadUrl} title={receipt.filename} style={{
                  flex: 1, border: 'none', width: '100%',
                }} />
              ) : isImage ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'auto' }}>
                  <img src={receipt.downloadUrl} alt={receipt.filename} style={{
                    maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4,
                  }} />
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                  {'F\u00f6rhandsgranskning ej tillg\u00e4nglig \u2014 '}
                  <a href={receipt.downloadUrl} target="_blank" rel="noopener" style={{ color: 'var(--primary)', marginLeft: 4 }}>{'\u00f6ppna \u2197'}</a>
                </div>
              )
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                Ingen fil tillg\u00e4nglig
              </div>
            )}
          </div>

          {/* Draggable divider — wide hit area with visible center line */}
          <div
            onMouseDown={onDragStart}
            style={{
              width: 12, cursor: 'col-resize',
              background: 'transparent',
              flexShrink: 0, position: 'relative', zIndex: 3,
            }}
          >
            {/* Visible line */}
            <div style={{
              position: 'absolute', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)',
              width: 4, background: isDragging ? 'var(--primary)' : 'var(--border)',
              transition: 'background 0.15s',
            }} />
            {/* Grab handle dots */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              width: 6, height: 48, borderRadius: 3,
              background: isDragging ? 'var(--primary)' : 'rgba(255,255,255,0.15)',
              transition: 'background 0.15s',
            }} />
          </div>

          {/* Right: editable form */}
          <div style={{ flex: 1, padding: '1rem 1.25rem', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div>
                <label style={labelStyle}>Datum</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={fieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>Valuta</label>
                <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} style={fieldStyle} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Beskrivning</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="t.ex. Cisco Meraki MR33" style={fieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>Belopp inkl moms</label>
                <input inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); autoCalcVat(e.target.value, undefined) }} style={fieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>Momssats %</label>
                <select
                  value={vatRate}
                  onChange={(e) => { setVatRate(e.target.value); autoCalcVat(undefined, e.target.value) }}
                  style={fieldStyle}
                >
                  <option value="">V\u00e4lj...</option>
                  <option value="25">25%</option>
                  <option value="12">12% (livsmedel)</option>
                  <option value="6">6% (kultur/b\u00f6cker)</option>
                  <option value="0">0% (momsfri)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Belopp exkl moms</label>
                <input inputMode="decimal" value={amountExVat} onChange={(e) => setAmountExVat(e.target.value)} style={{ ...fieldStyle, color: 'var(--text-muted)' }} />
              </div>
              <div>
                <label style={labelStyle}>Momsbelopp</label>
                <input inputMode="decimal" value={vatAmount} onChange={(e) => setVatAmount(e.target.value)} style={{ ...fieldStyle, color: 'var(--text-muted)' }} />
              </div>
              <div>
                <label style={labelStyle}>Konto (BAS)</label>
                <input inputMode="numeric" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} placeholder="5410" style={fieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>Kontobeskrivning</label>
                <input value={accountLabel} onChange={(e) => setAccountLabel(e.target.value)} placeholder="F\u00f6rbrukningsinventarier" style={fieldStyle} />
              </div>
            </div>

            {/* Kategori (readonly) */}
            <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {'Kategori: '}
              <span style={{ color: CAT_COLOR[receipt.category] }}>{'\u25cf '}</span>
              {CAT_LABEL[receipt.category]}
              {' \u00b7 Storlek: ' + (receipt.size / 1024).toFixed(0) + ' KB'}
              {receipt.fileHash && ' \u00b7 SHA: ' + receipt.fileHash.slice(0, 10) + '\u2026'}
            </div>

            {msg && (
              <div style={{
                marginTop: '0.6rem', padding: '0.4rem 0.7rem', borderRadius: 6,
                fontSize: '0.85rem',
                background: msg.startsWith('Fel') ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                color: msg.startsWith('Fel') ? '#ef4444' : '#10b981',
              }}>
                {msg}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
              <button
                onClick={() => onSave(false)}
                disabled={saving}
                style={{
                  background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.9rem',
                  fontWeight: 600, cursor: 'pointer', flex: 1,
                }}
              >
                {saving ? 'Sparar...' : '\u2713 Verifiera & spara'}
              </button>
              <button
                onClick={() => onSave(true)}
                disabled={saving}
                style={{
                  background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '0.55rem 1rem', fontSize: '0.9rem',
                  cursor: 'pointer', flex: 1,
                }}
              >
                {saving ? '...' : 'Spara & n\u00e4sta \u25b6'}
              </button>
            </div>

            {/* Tips */}
            <div style={{ marginTop: '0.6rem', fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.6 }}>
              Dra i mittlinjen f\u00f6r att \u00e4ndra storlek \u00b7 Escape st\u00e4nger
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================

const INPUT_STYLE: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
  borderRadius: 4, padding: '2px 6px', fontSize: '0.78rem', color: '#fff',
  width: '100%', outline: 'none',
}
const BTN_STYLE: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
  borderRadius: 6, padding: '4px 10px', fontSize: '0.72rem',
  color: '#fff', cursor: 'pointer',
}

interface ReceiptDetailProps {
  r: CloudReceipt
  q: ReceiptQuality
  companyId: string
  reanalyzing: boolean
  onReanalyze: () => void
  onDelete: () => void
}

function ReceiptDetail({ r, q, companyId, reanalyzing, onReanalyze, onDelete }: ReceiptDetailProps) {
  const [editing, setEditing] = useState<string | null>(null)
  const [editVal, setEditVal] = useState('')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function startEdit(field: string, currentValue: string) {
    setEditing(field)
    setEditVal(currentValue)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  async function saveEdit(field: string) {
    if (saving) return
    setSaving(true)
    try {
      const patch: Record<string, unknown> = {}
      const v = editVal.trim()
      switch (field) {
        case 'date': patch.date = v || undefined; break
        case 'title': patch.title = v || undefined; break
        case 'amount': patch.amount = parseNum(v); break
        case 'amountExVat': patch.amountExVat = parseNum(v); break
        case 'vatAmount': patch.vatAmount = parseNum(v); break
        case 'vatRate': {
          // Acceptera "25", "25%", "0.25"
          let n = parseNum(v.replace('%', ''))
          if (n != null && n >= 1) n = n / 100 // 25 → 0.25, 6 → 0.06, 12 → 0.12
          patch.vatRate = n
          break
        }
        case 'currency': patch.currency = v || undefined; break
        case 'accountCode': {
          const n = parseNum(v)
          patch.accountCode = n != null ? Math.round(n) : undefined
          break
        }
        case 'accountLabel': patch.accountLabel = v || undefined; break
      }
      await updateReceiptFields(companyId, r.id, patch as Parameters<typeof updateReceiptFields>[2])
    } catch (e) {
      console.error('Save failed:', e)
    } finally {
      setSaving(false)
      setEditing(null)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent, field: string) {
    if (e.key === 'Enter') saveEdit(field)
    if (e.key === 'Escape') setEditing(null)
  }

  function editableField(field: string, label: string, displayValue: string, rawValue: string, missing?: boolean) {
    const isEditing = editing === field
    return (
      <tr>
        <td style={{ padding: '2px 8px 2px 0', color: 'var(--text-muted)', whiteSpace: 'nowrap', verticalAlign: 'top', fontSize: '0.78rem' }}>
          {label}
        </td>
        <td style={{ padding: '2px 0', fontSize: '0.78rem' }}>
          {isEditing ? (
            <input
              ref={inputRef}
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onBlur={() => saveEdit(field)}
              onKeyDown={(e) => handleKeyDown(e, field)}
              style={INPUT_STYLE}
              disabled={saving}
            />
          ) : (
            <span
              onClick={(e) => { e.stopPropagation(); startEdit(field, rawValue) }}
              style={{
                cursor: 'pointer', padding: '1px 4px', borderRadius: 3,
                color: missing ? '#f97316' : 'var(--text)',
                fontStyle: missing ? 'italic' : 'normal',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              title={'Klicka f\u00f6r att redigera'}
            >
              {displayValue}
            </span>
          )}
        </td>
      </tr>
    )
  }

  function readonlyField(label: string, value: string, color?: string) {
    return (
      <tr>
        <td style={{ padding: '2px 8px 2px 0', color: 'var(--text-muted)', whiteSpace: 'nowrap', verticalAlign: 'top', fontSize: '0.78rem' }}>
          {label}
        </td>
        <td style={{ padding: '2px 0', fontSize: '0.78rem', color: color || 'var(--text)' }}>
          {value}
        </td>
      </tr>
    )
  }

  return (
    <div style={{
      padding: '0.5rem 0 0.75rem 1.5rem',
      background: 'rgba(255,255,255,0.02)',
      borderTop: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Left: editable accounting fields */}
        <div style={{ flex: '1 1 220px', minWidth: 200 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
            {'Bokf\u00f6ringsdata'}
            <span style={{ fontWeight: 400, marginLeft: 8, fontSize: '0.65rem', opacity: 0.6 }}>{'klicka f\u00f6r att redigera'}</span>
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {editableField('date', 'Datum', r.date || '\u2014', r.date || '', !r.date)}
              {editableField('title', 'Beskrivning', r.title || '\u2014', r.title || '', !r.title)}
              {editableField('amount', 'Belopp inkl moms', r.amount != null ? fmt(r.amount) + ' ' + (r.currency || 'SEK') : '\u2014', r.amount != null ? String(r.amount) : '', r.amount == null)}
              {editableField('amountExVat', 'Belopp exkl moms', r.amountExVat != null ? fmt(r.amountExVat) + ' ' + (r.currency || 'SEK') : '\u2014', r.amountExVat != null ? String(r.amountExVat) : '', r.amountExVat == null)}
              {editableField('vatAmount', 'Momsbelopp', r.vatAmount != null ? fmt(r.vatAmount) + ' ' + (r.currency || 'SEK') : '\u2014', r.vatAmount != null ? String(r.vatAmount) : '', r.vatAmount == null)}
              {editableField('vatRate', 'Momssats', r.vatRate != null ? (VAT_LABEL[r.vatRate] || (r.vatRate * 100).toFixed(0) + '%') : '\u2014', r.vatRate != null ? String(r.vatRate * 100) : '', r.vatRate == null)}
              {editableField('currency', 'Valuta', r.currency || '\u2014', r.currency || '', !r.currency)}
              {editableField('accountCode', 'Konto (BAS)', r.accountCode != null ? String(r.accountCode) : '\u2014', r.accountCode != null ? String(r.accountCode) : '', r.accountCode == null)}
              {editableField('accountLabel', 'Kontobeskrivning', r.accountLabel || '\u2014', r.accountLabel || '', !r.accountLabel)}
              {readonlyField('Kategori', CAT_LABEL[r.category])}
              {readonlyField('Kvalitet', Q_LABEL[q], Q_COLOR[q])}
              {readonlyField('Filnamn', r.filename)}
            </tbody>
          </table>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
            <button onClick={(e) => { e.stopPropagation(); onReanalyze() }} disabled={reanalyzing}
              style={{ ...BTN_STYLE, color: reanalyzing ? 'var(--text-muted)' : '#fff', opacity: reanalyzing ? 0.5 : 1 }}>
              {reanalyzing ? 'Analyserar...' : 'xAI Omanalys'}
            </button>
            {r.downloadUrl && (
              <a href={r.downloadUrl} target="_blank" rel="noopener"
                style={{ ...BTN_STYLE, color: 'var(--primary)', textDecoration: 'none' }}>
                {'\u00d6ppna original \u2197'}
              </a>
            )}
            {r.downloadUrl && (
              <a href={r.downloadUrl} target="_blank" rel="noopener" download={r.filename}
                style={{ ...BTN_STYLE, color: 'var(--primary)', textDecoration: 'none' }}>
                {'\u2b07\ufe0f Ladda ned'}
              </a>
            )}
            <button onClick={(e) => { e.stopPropagation(); onDelete() }}
              style={{ ...BTN_STYLE, background: 'transparent', borderColor: 'var(--danger)', color: 'var(--danger)' }}>
              {'\ud83d\uddd1\ufe0f Ta bort'}
            </button>
          </div>
        </div>

        {/* Right: file preview */}
        {r.downloadUrl && (
          <div style={{ flex: '0 0 200px', maxWidth: 260 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
              {'F\u00f6rhandsgranskning'}
            </div>
            {r.mime && r.mime.startsWith('image/') ? (
              <img src={r.downloadUrl} alt={r.filename} style={{
                width: '100%', maxHeight: 240, objectFit: 'contain',
                borderRadius: 6, border: '1px solid var(--border)', background: '#fff',
              }} />
            ) : r.mime === 'application/pdf' ? (
              <iframe src={r.downloadUrl} title={r.filename} style={{
                width: '100%', height: 280, border: '1px solid var(--border)',
                borderRadius: 6, background: '#fff',
              }} />
            ) : (
              <div style={{
                padding: '1rem', textAlign: 'center',
                border: '1px solid var(--border)', borderRadius: 6,
                fontSize: '0.8rem', color: 'var(--text-muted)',
              }}>
                {'Kan inte f\u00f6rhandsgranska ' + (r.mime || 'ok\u00e4nd filtyp')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
